import React from 'react';

interface BrokenCauldronToxicPropsProps {
  isLiberated: boolean;
}

export const BrokenCauldronToxicProps: React.FC<BrokenCauldronToxicPropsProps> = React.memo(({ isLiberated }) => {
  return (
    <div className="relative flex flex-col items-center select-none pointer-events-none transition-all duration-1000">
      {/* Ground Shadow & Slime / Flower Puddle */}
      <div className="relative flex items-center justify-center">
        {/* Toxic / Clear ground puddle */}
        <div
          className={`absolute -bottom-2 w-32 h-12 rounded-full blur-[2px] transition-all duration-1000 ${
            isLiberated
              ? 'bg-emerald-400/20 scale-90'
              : 'bg-green-500/40 shadow-[0_0_20px_rgba(34,197,94,0.6)] animate-pulse'
          }`}
        />

        {/* Scattered Toxic Waste Debris (or Purified Flowers upon liberation) */}
        {!isLiberated && (
          <div className="absolute -bottom-3 w-40 h-10 flex items-center justify-between px-1 z-10">
            {/* Toxic Barrel & Flask Left */}
            <div className="flex items-end gap-1 -translate-x-2">
              {/* Leaking flask */}
              <svg className="w-5 h-6 drop-shadow" viewBox="0 0 20 24">
                <path d="M7 2 L13 2 L13 8 L18 18 C19 20 18 22 15 22 L5 22 C2 22 1 20 2 18 L7 8 Z" fill="#0f172a" stroke="#22c55e" strokeWidth="1.5" />
                <path d="M4 16 L16 16 L15 21 L5 21 Z" fill="#22c55e" className="animate-pulse" />
                <circle cx="10" cy="18" r="1.5" fill="#86efac" />
              </svg>
              {/* Crushed toxic tin can */}
              <div className="w-3.5 h-4 bg-amber-800 border border-red-500 rotate-12 rounded-xs flex items-center justify-center">
                <div className="w-1.5 h-1.5 bg-yellow-400 rounded-full animate-ping" />
              </div>
            </div>

            {/* Toxic sludge puddles */}
            <div className="w-8 h-2.5 bg-emerald-500/80 rounded-full border border-green-300 blur-[0.5px] -translate-y-1" />

            {/* Broken glass shards & plastic trash Right */}
            <div className="flex items-end gap-1 translate-x-2">
              {/* Rusted battery / debris */}
              <div className="w-4 h-5 bg-slate-900 border border-emerald-400 -rotate-12 rounded-xs flex flex-col justify-center items-center p-0.5">
                <span className="text-[7px] text-red-500 font-black leading-none">☣</span>
                <div className="w-2.5 h-1 bg-green-400 rounded-xs mt-0.5 animate-pulse" />
              </div>
              {/* Plastic bottle debris */}
              <div className="w-2.5 h-5 bg-cyan-900/80 border border-cyan-400 rotate-45 rounded-xs" />
            </div>
          </div>
        )}

        {/* Rising Toxic Green Smoke & Wisps */}
        {!isLiberated && (
          <div className="absolute -top-16 w-24 h-24 pointer-events-none overflow-visible flex items-center justify-center">
            {/* Animated Toxic Smoke Puffs */}
            <div className="absolute -top-4 w-12 h-12 rounded-full bg-emerald-500/30 blur-md animate-ping" style={{ animationDuration: '3s' }} />
            <div className="absolute -top-8 w-16 h-16 rounded-full bg-green-400/20 blur-lg animate-pulse" />
            
            {/* Floating green poison bubbles */}
            {[
              { left: '25%', delay: '0s', duration: '2.1s' },
              { left: '45%', delay: '0.5s', duration: '1.8s' },
              { left: '65%', delay: '0.9s', duration: '2.4s' },
              { left: '35%', delay: '1.3s', duration: '2.0s' },
            ].map((bubble, i) => (
              <div
                key={i}
                className="absolute bottom-4 w-2 h-2 bg-emerald-300 rounded-full shadow-[0_0_6px_#4ade80] opacity-80"
                style={{
                  left: bubble.left,
                  animation: `floatParticleUp ${bubble.duration} ease-in-out infinite`,
                  animationDelay: bubble.delay,
                }}
              />
            ))}
          </div>
        )}

        {/* The Large Shattered / Exploded Cauldron (Grand Chaudron Cassé) */}
        <div className={`relative z-20 w-24 h-18 flex items-center justify-center transition-all duration-1000 ${isLiberated ? 'scale-105' : ''}`}>
          <svg className={`w-full h-full drop-shadow-xl transition-all duration-1000 ${isLiberated ? 'filter drop-shadow-[0_0_12px_rgba(56,189,248,0.7)]' : ''}`} viewBox="0 0 96 72">
            <defs>
              {/* Toxic Green Liquid Glow */}
              <radialGradient id="toxicSlimeGlow" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="#86efac" stopOpacity="1" />
                <stop offset="60%" stopColor="#22c55e" stopOpacity="0.9" />
                <stop offset="100%" stopColor="#14532d" stopOpacity="0.7" />
              </radialGradient>
              {/* Purified Water Glow */}
              <radialGradient id="purifiedWaterGlow" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="#ffffff" stopOpacity="1" />
                <stop offset="50%" stopColor="#38bdf8" stopOpacity="0.9" />
                <stop offset="100%" stopColor="#0284c7" stopOpacity="0.75" />
              </radialGradient>
            </defs>

            {/* Cauldron Main Bulging Iron Body */}
            <path
              d="M 18,24 C 12,40 18,60 48,60 C 78,60 84,40 78,24 Z"
              fill={isLiberated ? '#1e293b' : '#27272a'}
              stroke={isLiberated ? '#0ea5e9' : '#09090b'}
              strokeWidth={isLiberated ? '2' : '2'}
              className="transition-colors duration-1000"
            />
            
            {/* Smooth Rounded Base Foot */}
            <path
              d="M 36,60 C 42,63 54,63 60,60"
              fill="none"
              stroke={isLiberated ? '#0f172a' : '#18181b'}
              strokeWidth="3"
              strokeLinecap="round"
              className="transition-colors duration-1000"
            />
            
            {/* Iron Texture Highlights */}
            <path
              d="M 22,28 C 18,40 22,54 44,56"
              fill="none"
              stroke={isLiberated ? '#38bdf8' : '#52525b'}
              strokeWidth="2"
              opacity={isLiberated ? '0.4' : '0.6'}
              className="transition-all duration-1000"
            />

            {/* Cauldron Rim */}
            <ellipse
              cx="48"
              cy="24"
              rx="32"
              ry="8"
              fill={isLiberated ? '#0f172a' : '#18181b'}
              stroke={isLiberated ? '#38bdf8' : '#09090b'}
              strokeWidth={isLiberated ? '2' : '2'}
              className="transition-all duration-1000"
            />

            {/* Boiling Toxic Liquid Surface (or Pure Sparkling Water) */}
            <ellipse
              cx="48"
              cy="24"
              rx="28"
              ry="6.5"
              fill={isLiberated ? 'url(#purifiedWaterGlow)' : 'url(#toxicSlimeGlow)'}
              className={isLiberated ? 'animate-pulse' : 'animate-pulse'}
            />

            {/* Purified Sparkles */}
            {isLiberated && (
              <g className="animate-pulse">
                <circle cx="34" cy="22" r="1.5" fill="#ffffff" />
                <circle cx="62" cy="23" r="1.8" fill="#bae6fd" />
                <circle cx="48" cy="20" r="2" fill="#ffffff" />
                <circle cx="44" cy="25" r="1.2" fill="#7dd3fc" />
              </g>
            )}

            {/* Exploded Jagged Crack / Fissure running down the front of the cauldron */}
            {!isLiberated && (
              <>
                {/* Shattered fracture lines with toxic green light leaking out (starts strictly at front rim y=32.5) */}
                <path
                  d="M 42,32.5 L 45,38 L 39,44 L 47,50 L 44,57"
                  fill="none"
                  stroke="#4ade80"
                  strokeWidth="3.5"
                  strokeLinecap="round"
                  filter="drop-shadow(0 0 4px #22c55e)"
                />
                <path
                  d="M 45,38 L 54,42 L 51,47"
                  fill="none"
                  stroke="#86efac"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <path
                  d="M 42,32.5 L 45,38 L 39,44 L 47,50 L 44,57"
                  fill="none"
                  stroke="#bbf7d0"
                  strokeWidth="1.2"
                  strokeLinecap="round"
                />
                {/* Green toxic ooze spilling down out of the lower fracture */}
                <path
                  d="M 44,48 C 42,54 40,58 38,62 C 42,64 48,63 46,57 Z"
                  fill="#22c55e"
                />
                <circle cx="38" cy="62.5" r="2" fill="#4ade80" className="animate-ping" />
              </>
            )}

            {/* Iron Cauldron Handles */}
            <path
              d="M 16,26 C 6,26 6,38 16,38"
              fill="none"
              stroke="#52525b"
              strokeWidth="2.5"
              strokeLinecap="round"
            />
            {/* Right handle cracked / bent by explosion */}
            <path
              d="M 80,26 C 88,26 92,34 84,38"
              fill="none"
              stroke="#52525b"
              strokeWidth="2.5"
              strokeLinecap="round"
            />
          </svg>
        </div>
      </div>
    </div>
  );
});
